/**
 * OKX Wallet Extension Integration
 * Provides signing functionality using OKX wallet browser extension when OKX wallet is connected
 * Supports Ethereum, Solana, and Bitcoin chains
 * 
 * Note: This module works with wallet extension providers (window.okxwallet.*), not the OKX SDK classes.
 * The OKX SDK packages (@okxweb3/coin-ethereum, @okxweb3/coin-solana, @okxweb3/coin-bitcoin) are
 * designed for offline signing with private keys, while wallet extension providers are browser-injected
 * objects that follow standard interfaces (EIP-1193 for Ethereum, Solana wallet adapter pattern, etc.).
 * 
 * Reference: https://web3.okx.com
 */

/**
 * OKX Bitcoin Provider interface
 * Based on OKX wallet extension's Bitcoin provider API
 */
export interface OKXBitcoinProvider {
  connect(): Promise<string[] | { address?: string; publicKey?: string; accounts?: string[] }>;
  signMessage(signStr: string, type?: 'ecdsa' | 'bip322-simple'): Promise<string>;
  signPsbt(psbt: string): Promise<string>;
  requestAccounts?(): Promise<string[]>;
  getAccounts?(): Promise<string[]>;
}

/**
 * OKX Ethereum Provider interface
 * Based on EIP-1193 standard with OKX-specific extensions
 */
export interface OKXEthereumProvider {
  request(args: { method: 'eth_requestAccounts' }): Promise<string[]>;
  request(args: {
    method: 'eth_signTypedData_v4';
    params: [string, string];
  }): Promise<string>;
  request(args: {
    method: 'eth_sign';
    params: [string, string];
  }): Promise<string>;
  isOKX?: boolean;
  // EIP-1193 event emitter methods
  on?(event: 'disconnect', handler: (error: { code: number; message: string }) => void): void;
  on?(event: 'accountsChanged', handler: (accounts: string[]) => void): void;
  removeListener?(event: 'disconnect', handler: (error: { code: number; message: string }) => void): void;
  removeListener?(event: 'accountsChanged', handler: (accounts: string[]) => void): void;
}

/**
 * OKX Solana Provider interface
 * Based on standard Solana wallet adapter pattern
 */
export interface OKXSolanaProvider {
  isConnected: boolean;
  publicKey: {
    toString(): string;
  } | null;
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: 'utf8' | 'hex'
  ): Promise<{
    signature: Uint8Array | number[];
    publicKey?: { toString(): string };
  }>;
  // Event emitter methods (if available)
  on?(event: 'disconnect', handler: () => void): void;
  removeListener?(event: 'disconnect', handler: () => void): void;
}

/**
 * OKX Ethereum signing result
 */
export interface OKXEthereumSignResult {
  signature: string;
  address: string;
}

/**
 * OKX Solana signing result
 */
export interface OKXSolanaSignResult {
  signature: string;
  publicKey: string;
}

/**
 * OKX Bitcoin signing result
 */
export interface OKXBitcoinSignResult {
  signature: string;
  address: string;
}

/**
 * Check if OKX wallet is installed and available
 */
export function isOKXWalletAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).okxwallet;
}

/**
 * Check if OKX Ethereum provider is available
 * 
 * Smart Detection: If OKX Solana AND Bitcoin are both detected,
 * then window.ethereum is very likely OKX (even if okxwallet.ethereum not available)
 */
export function isOKXEthereumAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Primary: Check okxwallet.ethereum
  if ((window as any).okxwallet?.ethereum) {
    return true;
  }
  
  // Secondary: Check if ethereum has isOKX flag
  if ((window as any).ethereum?.isOKX) {
    return true;
  }
  
  // Smart Detection: If OKX Solana AND Bitcoin are both detected,
  // then window.ethereum is very likely OKX
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
  const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
  
  if (hasOKXMultiChain && (window as any).ethereum) {
    // OKX is detected via multi-chain, so window.ethereum is very likely OKX
    return true;
  }
  
  return false;
}

/**
 * Check if OKX Solana provider is available
 */
export function isOKXSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).okxwallet?.solana;
}

/**
 * Check if OKX Bitcoin provider is available
 */
export function isOKXBitcoinAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).okxwallet?.bitcoin;
}

/**
 * Get OKX Ethereum provider
 * 
 * Smart Detection: If OKX Solana AND Bitcoin are both detected,
 * then window.ethereum is very likely OKX (even if okxwallet.ethereum not available)
 */
export function getOKXEthereumProvider(): OKXEthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  // Primary: Check okxwallet.ethereum
  if ((window as any).okxwallet?.ethereum) {
    return (window as any).okxwallet.ethereum as OKXEthereumProvider;
  }
  
  // Secondary: Check if ethereum has isOKX flag
  if ((window as any).ethereum?.isOKX) {
    return (window as any).ethereum as OKXEthereumProvider;
  }
  
  // Smart Detection: If OKX Solana AND Bitcoin are both detected,
  // then window.ethereum is very likely OKX
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
  const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
  
  if (hasOKXMultiChain && (window as any).ethereum) {
    // OKX is detected via multi-chain, so window.ethereum is very likely OKX
    return (window as any).ethereum as OKXEthereumProvider;
  }
  
  return null;
}

/**
 * Get OKX Solana provider
 */
export function getOKXSolanaProvider(): OKXSolanaProvider | null {
  if (typeof window === "undefined") return null;
  return (window as any).okxwallet?.solana as OKXSolanaProvider | null;
}

/**
 * Get OKX Bitcoin provider
 */
export function getOKXBitcoinProvider(): OKXBitcoinProvider | null {
  if (typeof window === "undefined") return null;
  return (window as any).okxwallet?.bitcoin as OKXBitcoinProvider | null;
}

/**
 * Get Ethereum address from OKX wallet
 */
export async function getOKXEthereumAddress(): Promise<string> {
  const provider = getOKXEthereumProvider();
  if (!provider) {
    throw new Error("OKX Ethereum wallet not found");
  }

  try {
    // Request accounts from OKX wallet
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) {
      throw new Error("No accounts found in OKX Ethereum wallet");
    }
    return accounts[0];
  } catch (error) {
    throw new Error(`Failed to get OKX Ethereum address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign EIP-712 typed data with OKX Ethereum wallet using OKX SDK
 * 
 * Note: OKX SDK's EthWallet is primarily for offline signing with private keys.
 * For wallet extension signing, we use the wallet's native EIP-712 signing.
 * However, we can use OKX SDK for message formatting and validation.
 */
export async function signOKXEthereumTypedData(
  domain: any,
  types: any,
  message: any,
  address: string
): Promise<OKXEthereumSignResult> {
  const provider = getOKXEthereumProvider();
  if (!provider) {
    throw new Error("OKX Ethereum wallet not found");
  }

  try {
    // Use OKX wallet's native EIP-712 signing
    // The wallet extension handles the signing internally
    const signature = await provider.request({
      method: 'eth_signTypedData_v4',
      params: [address, JSON.stringify({
        domain,
        types,
        primaryType: 'AgentApproval',
        message
      })]
    });

    return {
      signature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OKX Ethereum: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Sign message with OKX Ethereum wallet
 */
export async function signOKXEthereumMessage(
  message: string,
  address: string
): Promise<OKXEthereumSignResult> {
  const provider = getOKXEthereumProvider();
  if (!provider) {
    throw new Error("OKX Ethereum wallet not found");
  }

  try {
    // Use eth_sign for message signing
    const signature = await provider.request({
      method: 'eth_sign',
      params: [address, message]
    });

    return {
      signature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign message with OKX Ethereum: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get Solana address from OKX wallet
 */
export async function getOKXSolanaAddress(): Promise<string> {
  const provider = getOKXSolanaProvider();
  if (!provider) {
    throw new Error("OKX Solana wallet not found");
  }

  try {
    // Connect to OKX Solana wallet
    const response = await provider.connect();
    if (!response || !response.publicKey) {
      throw new Error("Failed to connect to OKX Solana wallet");
    }
    return response.publicKey.toString();
  } catch (error) {
    throw new Error(`Failed to get OKX Solana address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign message with OKX Solana wallet
 */
export async function signOKXSolanaMessage(
  message: string | Uint8Array,
  publicKey?: string
): Promise<OKXSolanaSignResult> {
  const provider = getOKXSolanaProvider();
  if (!provider) {
    throw new Error("OKX Solana wallet not found");
  }

  try {
    // Ensure connected
    if (!provider.isConnected) {
      await provider.connect();
    }

    // Convert message to Uint8Array if string
    const messageBytes = typeof message === 'string' 
      ? new TextEncoder().encode(message)
      : message;

    // Sign message with OKX Solana wallet
    const signedMessage = await provider.signMessage(messageBytes, 'utf8');

    // Convert signature to hex string
    const signatureBytes = Array.isArray(signedMessage.signature) 
      ? signedMessage.signature as number[]
      : Array.from(signedMessage.signature as Uint8Array) as number[];
    const signatureHex = signatureBytes
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const pubKey = signedMessage.publicKey?.toString() || publicKey || '';
    if (!pubKey) {
      throw new Error("Failed to get public key from OKX Solana wallet");
    }

    return {
      signature: signatureHex,
      publicKey: pubKey
    };
  } catch (error: any) {
    if (error.code === 4001 || error.message?.includes("rejected")) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OKX Solana: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get Bitcoin address from OKX wallet
 * 
 * OKX Bitcoin uses different methods than EVM chains:
 * - Preferred: requestAccounts() (v2.77.1+)
 * - Alternative: connect() returns { address, publicKey }
 * - getAccounts() for already connected wallets
 * 
 * By default, OKX returns Taproot addresses (bc1p...)
 */
export async function getOKXBitcoinAddress(): Promise<string> {
  const provider = getOKXBitcoinProvider();
  if (!provider) {
    throw new Error("OKX Bitcoin wallet not found");
  }

  try {
    let address: string | undefined;

    // Method 1: Try requestAccounts() (preferred for v2.77.1+)
    if (typeof provider.requestAccounts === 'function') {
      const accounts = await provider.requestAccounts();
      if (accounts && accounts.length > 0) {
        address = accounts[0];
      }
    }

    // Method 2: Fallback to connect() (older method or if requestAccounts not available)
    if (!address && typeof provider.connect === 'function') {
      const result = await provider.connect();
      
      // connect() can return either:
      // - Array of addresses: [address]
      // - Object with address property: { address, publicKey }
      if (Array.isArray(result)) {
        if (result.length > 0) {
          address = result[0];
        }
      } else if (result && typeof result === 'object') {
        // Handle { address, publicKey } format
        if (result.address) {
          address = result.address;
        } else if (Array.isArray(result.accounts) && result.accounts.length > 0) {
          address = result.accounts[0];
        }
      }
    }

    // Method 3: Try getAccounts() if already connected
    if (!address && typeof provider.getAccounts === 'function') {
      const accounts = await provider.getAccounts();
      if (accounts && accounts.length > 0) {
        address = accounts[0];
      }
    }

    if (!address) {
      throw new Error("No accounts found in OKX Bitcoin wallet. Please ensure the wallet is connected and has a Bitcoin account.");
    }

    return address;
  } catch (error) {
    if (error instanceof Error && error.message.includes("No accounts found")) {
      throw error;
    }
    throw new Error(`Failed to get OKX Bitcoin address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign message with OKX Bitcoin wallet
 */
export async function signOKXBitcoinMessage(
  message: string,
  address: string
): Promise<OKXBitcoinSignResult> {
  const provider = getOKXBitcoinProvider();
  if (!provider) {
    throw new Error("OKX Bitcoin wallet not found");
  }

  try {
    // Ensure connected - try requestAccounts() first, then connect()
    let accounts: string[] = [];
    
    if (typeof provider.requestAccounts === 'function') {
      accounts = await provider.requestAccounts();
    } else if (typeof provider.connect === 'function') {
      const result = await provider.connect();
      if (Array.isArray(result)) {
        accounts = result;
      } else if (result && typeof result === 'object') {
        if (result.address) {
          accounts = [result.address];
        } else if (Array.isArray(result.accounts)) {
          accounts = result.accounts;
        }
      }
    } else if (typeof provider.getAccounts === 'function') {
      accounts = await provider.getAccounts();
    }
    
    // Verify address is available
    if (!accounts.includes(address)) {
      throw new Error("Address not found in OKX Bitcoin wallet. Please ensure the wallet is connected and the address is available.");
    }

    // Sign message with OKX Bitcoin wallet
    // OKX signMessage API: signMessage(signStr, type?)
    // The address is not needed as a parameter - the wallet uses the connected address
    // Type defaults to 'ecdsa' if not specified
    const signature = await provider.signMessage(message, 'ecdsa');

    // Normalize signature format (OKX returns base64 or hex)
    let normalizedSignature = signature;
    if (signature.startsWith('0x')) {
      normalizedSignature = signature.slice(2);
    }

    return {
      signature: normalizedSignature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001 || error.message?.includes("rejected")) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OKX Bitcoin: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Connect to OKX wallet for a specific chain
 */
export async function connectOKXWallet(chainType: 'ethereum' | 'solana' | 'bitcoin'): Promise<string> {
  switch (chainType) {
    case 'ethereum':
      return await getOKXEthereumAddress();
    case 'solana':
      return await getOKXSolanaAddress();
    case 'bitcoin':
      return await getOKXBitcoinAddress();
    default:
      throw new Error(`Unsupported chain type: ${chainType}`);
  }
}

/**
 * Wallet disconnection callback type
 */
export type WalletDisconnectCallback = (chainType: 'ethereum' | 'solana' | 'bitcoin', reason?: string) => void;

/**
 * Set up disconnection detection for OKX Ethereum provider
 * Listens to EIP-1193 disconnect and accountsChanged events
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove event listeners
 */
export function setupOKXEthereumDisconnectDetection(
  onDisconnect: WalletDisconnectCallback
): () => void {
  const provider = getOKXEthereumProvider();
  if (!provider) {
    return () => {}; // No provider, no cleanup needed
  }

  const handleDisconnect = (error: { code: number; message: string }) => {
    console.warn('[OKX] Ethereum wallet disconnected:', error);
    onDisconnect('ethereum', error.message || 'Wallet disconnected');
  };

  const handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      console.warn('[OKX] Ethereum wallet accounts cleared (disconnected)');
      onDisconnect('ethereum', 'Accounts cleared');
    }
  };

  // Set up event listeners if provider supports EIP-1193 events
  if (provider.on) {
    provider.on('disconnect', handleDisconnect);
    provider.on('accountsChanged', handleAccountsChanged);
  }

  // Return cleanup function
  return () => {
    if (provider.removeListener) {
      provider.removeListener('disconnect', handleDisconnect);
      provider.removeListener('accountsChanged', handleAccountsChanged);
    }
  };
}

/**
 * Set up disconnection detection for OKX Solana provider
 * Monitors isConnected property and disconnect events
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove event listeners and interval
 */
export function setupOKXSolanaDisconnectDetection(
  onDisconnect: WalletDisconnectCallback
): () => void {
  const provider = getOKXSolanaProvider();
  if (!provider) {
    return () => {}; // No provider, no cleanup needed
  }

  let wasConnected = provider.isConnected;
  let intervalId: NodeJS.Timeout | null = null;

  const checkConnection = () => {
    const isCurrentlyConnected = provider.isConnected;
    
    // Detect transition from connected to disconnected
    if (wasConnected && !isCurrentlyConnected) {
      console.warn('[OKX] Solana wallet disconnected (isConnected changed)');
      onDisconnect('solana', 'Connection lost');
    }
    
    wasConnected = isCurrentlyConnected;
  };

  const handleDisconnect = () => {
    console.warn('[OKX] Solana wallet disconnected (event)');
    onDisconnect('solana', 'Disconnected');
    wasConnected = false;
  };

  // Set up event listener if available
  if (provider.on) {
    provider.on('disconnect', handleDisconnect);
  }

  // Poll isConnected property every 2 seconds as fallback
  // (Some providers may not emit disconnect events reliably)
  intervalId = setInterval(checkConnection, 2000);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (provider.removeListener) {
      provider.removeListener('disconnect', handleDisconnect);
    }
  };
}

/**
 * Set up disconnection detection for OKX Bitcoin provider
 * Checks if provider is still available periodically
 * 
 * @param onDisconnect - Callback function called when wallet disconnects
 * @returns Cleanup function to remove interval
 */
export function setupOKXBitcoinDisconnectDetection(
  onDisconnect: WalletDisconnectCallback
): () => void {
  let intervalId: NodeJS.Timeout | null = null;
  let wasAvailable = isOKXBitcoinAvailable();

  const checkAvailability = () => {
    const isCurrentlyAvailable = isOKXBitcoinAvailable();
    
    // Detect transition from available to unavailable
    if (wasAvailable && !isCurrentlyAvailable) {
      console.warn('[OKX] Bitcoin wallet disconnected (provider no longer available)');
      onDisconnect('bitcoin', 'Provider unavailable');
    }
    
    wasAvailable = isCurrentlyAvailable;
  };

  // Check availability every 2 seconds
  intervalId = setInterval(checkAvailability, 2000);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

/**
 * Set up disconnection detection for all OKX wallet providers
 * 
 * @param onDisconnect - Callback function called when any wallet disconnects
 * @returns Cleanup function to remove all event listeners
 */
export function setupOKXWalletDisconnectDetection(
  onDisconnect: WalletDisconnectCallback
): () => void {
  const cleanupFunctions: Array<() => void> = [];

  // Set up detection for each chain type
  cleanupFunctions.push(setupOKXEthereumDisconnectDetection(onDisconnect));
  cleanupFunctions.push(setupOKXSolanaDisconnectDetection(onDisconnect));
  cleanupFunctions.push(setupOKXBitcoinDisconnectDetection(onDisconnect));

  // Return combined cleanup function
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
}
